import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { AuthCard } from "@/components/app/AuthCard";
import { Loading } from "@/components/app/PageHeader";

interface TokenInfo { kind: "invite" | "reset"; email: string; name: string }

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [invalid, setInvalid] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.post<TokenInfo>("/auth/verify-token", { token }).then((r) => setInfo(r.data)).catch((e) => setInvalid(errorMessage(e)));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw !== pw2) return setError("Les mots de passe ne correspondent pas");
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Mot de passe enregistré. Vous pouvez vous connecter.");
      navigate("/login", { replace: true });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  if (invalid) {
    return (
      <AuthCard title="Lien invalide ou expiré" subtitle={invalid}>
        <Link to="/mot-de-passe-oublie" data-testid="set-password-request-new" className="text-sm text-blue-400 hover:text-blue-300">Demander un nouveau lien</Link>
      </AuthCard>
    );
  }
  if (!info) return <div className="px-8"><Loading /></div>;
  const isInvite = info.kind === "invite";

  return (
    <AuthCard title={isInvite ? `Bienvenue, ${info.name}` : "Nouveau mot de passe"} subtitle={isInvite ? `Choisissez le mot de passe du compte ${info.email}.` : `Compte : ${info.email}`}>
      <form onSubmit={submit} className="space-y-5" data-testid="set-password-form">
        <div className="space-y-2"><Label>Mot de passe (8 caractères min.)</Label><Input type="password" required minLength={8} autoComplete="new-password" data-testid="set-password-input" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 border-slate-700 bg-slate-900/70" /></div>
        <div className="space-y-2"><Label>Confirmer</Label><Input type="password" required minLength={8} autoComplete="new-password" data-testid="set-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11 border-slate-700 bg-slate-900/70" /></div>
        {error && <p data-testid="set-password-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
        <Button type="submit" disabled={busy || pw.length < 8} data-testid="set-password-submit" className="h-11 w-full bg-blue-600 text-white hover:bg-blue-500">{busy ? "Enregistrement…" : isInvite ? "Activer mon compte" : "Enregistrer"}</Button>
      </form>
    </AuthCard>
  );
}
